import * as React from 'react';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router';

import { useTrainingSetDelete } from '../../hooks/useTrainingSetDelete';

import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

export interface ITrainingSetDeleteProps {
  trainingSetId: string;
  type: 'menu' | 'icon';
  handleClose?: () => void;
  forwardUser?: boolean;
}

const TrainingSetDelete: React.FC<ITrainingSetDeleteProps> = ({
  trainingSetId,
  type,
  handleClose,
  forwardUser = false,
}) => {
  const { setToast, dataPoolId } = React.useContext(AppContext);
  const [open, setOpen] = React.useState(false);

  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
    runHandleClose();
  };

  const runHandleClose = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const glynt = useGlynt();
  const client = useQueryClient();

  const deleteTrainingSet = useTrainingSetDelete({
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['trainingSets']
      });
      setOpen(false);

      setToast({
        variant: 'success',
        open: true,
        message: `Training Set Deleted`,
      });
      if (forwardUser) {
        navigate(`/data-pools/${dataPoolId}/train`);
      }
    },
    onError: (error) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Sorry, failed to delete training set. ${error}`,
      });
    },
  });

  if (deleteTrainingSet.isPending) {
    return <CircularProgress size={12} style={{ marginLeft: '13px', marginRight: '14px' }} />;
  }

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          key={'Delete'}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
          id="training-set-delete"
          data-cy="btn-training-set-delete"
          onClick={handleClickOpen}
          disabled={deleteTrainingSet.isPending}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <MenuItemHiddenTextLabel>Delete</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <IconButtonWhiteBg
          disabled={deleteTrainingSet.isPending}
          onClick={handleClickOpen}
          id="icon-training-set-delete"
          cypressId="icon-training-set-delete"
          tooltip="Delete Training Set"
        >
          <DeleteIcon style={{ fontSize: '14px', color: 'black' }} />
        </IconButtonWhiteBg>
      ) : null}

      <Dialog
        open={open}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this training set?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation} data-cy="btn-training-set-delete-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => deleteTrainingSet.mutate({ trainingSetId })}
            autoFocus
            data-cy="btn-training-set-delete-modal-yes"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrainingSetDelete;
